import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import CollapseTopBar from '../../../components/topBar/CollapseToBar'
import SimpleDatePicker from '../../../components/forms/SimpleDatePicker'
import { Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, Grid2, Icon, Stack } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import DtoStationType from '../../../referencial/dto/DtoStationType'
import DtoHydrometricStation from '../../dto/DtoHydrometricStation'
import HydrometryGraphicChart from './HydrometryGraphicChart'
import DtoHydroMeasures from '../../dto/chronicMeasures/DtoHydroMeasures'
import { arrayOf, getComponentWithId, objectOf } from '../../../utils/StoreUtils'
import HydrometryAction from '../../actions/HydrometryAction'
import { every, intersectionWith, keys, orderBy } from 'lodash'
import ToastrAction from '../../../toastr/actions/ToastrAction'
import DtoFilter from '../../../station/dto/DtoFilter'
import DtoParametrageDataType from '../../../piezometry/dto/DtoParametrageDataType'
import { getHardHydroDataTypes } from '../../../utils/HydroUtils'
import Select from '../../../components/forms/Select'
import ProgressCard from '../../../components/card/ProgressCard'
import RadioButtons from '../../../components/forms/RadioButtons'
import SimpleOptionsToolPanel from 'piezometry/components/validation/tools/SimpleOptionsToolPanel'
import useTitle from 'utils/customHook/useTitle'
import { ALL, AVERAGE, MAX, MIN } from 'hydrometry/constants/HydrometryConstants'
import useActions from '../../../utils/customHook/useActions'
import useUpdateEffect from '../../../utils/customHook/useUpdateEffect'
import moment from 'moment'
import SimpleSelectionTable from 'components/datatable/SimpleSelectionTable'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import useDebounce from 'utils/customHook/useDebounce'
import SimpleFilterSelect from 'components/forms/specific/SimpleFilterSelect'
import Input from 'components/forms/Input'
import { STATION_TYPE_NAME } from 'station/constants/StationConstants'
import { normalizeString } from 'utils/StringUtil'
import CityAction from 'referencial/components/city/actions/CityAction'
import { darkBlue, lightGrey } from 'utils/constants/ColorTheme'
import { NoDataCard } from 'components/card/NoDataCard'

const stationFilterFunction = (list, filters) => {
    const {
        searchValue,
        filter,
        resultFilter,
        city,
    } = filters

    const availableStations = filter !== -1 ? intersectionWith(list, resultFilter, (elem, res) => elem.id === res.id) : list
    const searchValueFormat = normalizeString(searchValue)
    const filterSearchValue = searchValue ? availableStations.filter(s => s.labelSearch.includes(searchValueFormat)) : availableStations
    return city ? filterSearchValue.filter(p => p.townCode === city) : filterSearchValue
}

const FilterFields = ({
    filter = {},
    setFilter = () => { },

    stations = [],
}) => {
    const {
        cities,
    } = useSelector(store => ({
        cities: store.CityReducer.cities,
    }), shallowEqual)

    const [stateFilter, setStateFilter] = useState(filter)

    useDebounce(() => {
        setFilter(stateFilter)
    }, 500, [stateFilter])

    return (
        <Grid2 container columnSpacing={2} rowSpacing={1}>
            <Grid2 size={6}>
                <Input
                    title={i18n.search}
                    value={stateFilter.searchValue}
                    onChange={searchValue => setStateFilter(prev => ({ ...prev, searchValue }))}
                    data-cy='searchInput'
                />
            </Grid2>
            <Grid2 size={6}>
                <SimpleFilterSelect
                    stationType={STATION_TYPE_NAME.hydrometry}
                    onChange={(resultFilter, newFilter) => {
                        setStateFilter(prev => ({ ...prev, resultFilter, filter: newFilter }))
                    }}
                    stations={stations}
                />
            </Grid2>
            <Grid2 size={6}>
                <Select
                    label={i18n.city}
                    value={stateFilter.city}
                    options={cities}
                    onChange={city => setStateFilter(prev => ({ ...prev, city }))}
                    keyLabel='labelWithCode'
                />
            </Grid2>
        </Grid2>
    )
}

FilterFields.propTypes = {
    filter: PropTypes.shape({
        searchValue: PropTypes.string,
        resultFilter: PropTypes.arrayOf(PropTypes.number),
        filter: PropTypes.number,
        city: PropTypes.string,
    }),
    setFilter: PropTypes.func,

    stations: PropTypes.arrayOf(PropTypes.instanceOf(DtoHydrometricStation)),
}

const SelectedStationDialog = ({
    isOpen,
    setIsOpen,
    selectedStations: selectedStationsProps,
    setSelectedStations: setSelectedStationsProps,
}) => {
    const {
        hydrometricStations,
        citiesIndex,
    } = useSelector(store => ({
        hydrometricStations: store.HydrometryReducer.hydrometricStations,
        citiesIndex: store.CityReducer.citiesIndex,
    }), shallowEqual)
    const [selectedStations, setSelectedStations] = useState(selectedStationsProps)

    const formattedHydro = useMemo(() => {
        return hydrometricStations.map(hs => {
            const city = citiesIndex[hs.townCode]?.name
            const name = `${hs.code ? `[${hs.code || ''}] ` : ''}${hs.name || ''}`
            return {
                id: hs.id,
                name,
                city,
                labelSearch: normalizeString(`${name} ${city}`),
            }
        })
    }, [hydrometricStations, citiesIndex])

    return (
        <Dialog
            fullWidth
            maxWidth='lg'
            scroll='body'
            open={isOpen}
        >
            <DefaultDialogTitle
                title={i18n.selectStations}
                onClose={() => {
                    setIsOpen(false)
                    setSelectedStations(selectedStationsProps)
                }}
            />
            <DialogContent>
                <SimpleSelectionTable
                    onChange={setSelectedStations}
                    listData={formattedHydro}
                    selectedList={selectedStations}
                    listHeaders={['name', 'city', 'nullValue']}
                    listTitle={i18n.nonSelectedStations}

                    selectedData={selectedStations}
                    selectedListHeaders={['name', 'city', 'nullValue']}
                    selectedListTitle={i18n.selectedStations}
                    filterField={({ filter, setFilter }) => <FilterFields filter={filter} setFilter={setFilter} stations={hydrometricStations}/>}
                    filterFunction={stationFilterFunction}
                    defaultFilter={{ filter: -1 }}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => {
                        setSelectedStationsProps(selectedStations)
                        setIsOpen(false)
                    }}
                    variant='contained'
                    color='primary'
                >
                    {i18n.validate}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

SelectedStationDialog.propTypes = {
    isOpen: PropTypes.bool,
    setIsOpen: PropTypes.func,
    selectedStations: PropTypes.arrayOf(PropTypes.instanceOf({
        id: PropTypes.number,
    })),
    setSelectedStations: PropTypes.arrayOf(PropTypes.instanceOf({
        id: PropTypes.number,
    })),
}

const HydrometerCollapseFilter = ({
    defaultFilter = {},
    onValidate = () => { },
}) => {
    const dispatch = useDispatch()
    const {
        hydrometryDataTypes,
    } = useSelector(store => ({
        hydrometryDataTypes: store.HydrometryReducer.hydrometryDataTypes,
        hydrometricStations: store.HydrometryReducer.hydrometricStations,
    }), shallowEqual)

    const [isOpen, setIsOpen] = useState(false)

    const groups = useMemo(() => [
        { value: ALL, label: i18n.allMeasures },
        { value: MAX, label: i18n.dailyMax },
        { value: MIN, label: i18n.dailyMin },
        { value: AVERAGE, label: i18n.dailyAverage },
    ], [])

    const evolutionModes = useMemo(() => [
        { value: 'normal', label: i18n.comparing },
        { value: 'relative', label: i18n.relativeEvolution },
        { value: 'percent', label: i18n.percent },
    ], [])

    const chronicOptions = useMemo(() => orderBy([...getHardHydroDataTypes(), ...hydrometryDataTypes]), [hydrometryDataTypes])

    const [startDate, setStartDate] = useState(defaultFilter.startDate)
    const [endDate, setEndDate] = useState(defaultFilter.endDate)
    const [dataType, setDataType] = useState(defaultFilter.dataType)
    const [groupFunc, setGroupFunc] = useState(defaultFilter.groupFunc)
    const [displayMode, setDisplayMode] = useState(defaultFilter.displayMode)
    const [selectedStations, setSelectedStations] = useState(defaultFilter.selectedStations)

    useEffect(() => {
        setSelectedStations(defaultFilter.selectedStations)
    }, [defaultFilter])

    const onClickVisualize = () => {
        if (!startDate || !endDate) {
            dispatch(ToastrAction.error(i18n.noDateSelectedError))
        } else if (every(keys(selectedStations), c => !(selectedStations[c] || selectedStations[c] === 0))) {
            dispatch(ToastrAction.error(i18n.noStationSelectedError))
        } else {
            onValidate({
                startDate,
                endDate,
                dataType,
                groupFunc,
                displayMode,
                selectedStations: selectedStations.map(id => ({ id })),
            })
        }
    }

    return (
        <CollapseTopBar open={true}>
            <Card>
                <CardContent sx={{ background: lightGrey, borderLeft: `2px solid ${darkBlue}`, borderRight: `2px solid ${darkBlue}` }}>
                    <Grid2 container spacing={1} alignItems='center'>
                        <Grid2 size={3}>
                            <SimpleDatePicker
                                label={i18n.startDate}
                                onChange={setStartDate}
                                id='startDate'
                                value={startDate}
                                max={endDate}
                            />
                        </Grid2>
                        <Grid2 size={3}>
                            <SimpleDatePicker
                                label={i18n.endDate}
                                onChange={setEndDate}
                                id='endDate'
                                value={endDate}
                                min={startDate}
                            />
                        </Grid2>
                        <Grid2 size={6} container className='input-field'>
                            <label className='sieau-input select-label' >{i18n.selectStations}</label>
                            <i
                                className='material-icons blue-text clickable qualityGraphicsStationIcon tooltipped'
                                data-tooltip={i18n.stations}
                                onClick={() => setIsOpen(true)}
                                data-cy='stationsSelect'
                            >
                            list
                            </i>
                        </Grid2>
                        <Grid2 size={2}>
                            <Select
                                label={i18n.chronic}
                                options={chronicOptions}
                                onChange={setDataType}
                                value={dataType}
                                noNullValue
                                noSort
                            />
                        </Grid2>
                        <Grid2 size={2}>
                            <Select
                                label={i18n.graphicType}
                                options={groups}
                                onChange={setGroupFunc}
                                value={groupFunc}
                                noNullValue
                                noSort
                            />
                        </Grid2>
                        <Grid2 size={4}>
                            <RadioButtons
                                elements={evolutionModes}
                                selected={displayMode}
                                onChange={setDisplayMode}
                                title={i18n.displayMode}
                            />
                        </Grid2>
                        <Grid2 container justifyContent='flex-end' size={4}>
                            <Button onClick={onClickVisualize} variant='contained'>
                                <Icon>search</Icon>{i18n.visualize}
                            </Button>
                        </Grid2>
                    </Grid2>
                </CardContent>
            </Card>
            <SelectedStationDialog
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                selectedStations={selectedStations}
                setSelectedStations={setSelectedStations}
            />
        </CollapseTopBar>
    )
}

HydrometerCollapseFilter.propTypes = {
    defaultFilter: PropTypes.shape({
        startDate: PropTypes.number,
        endDate: PropTypes.number,
        dataType: PropTypes.number,
        groupFunc: PropTypes.string,
        displayMode: PropTypes.string,
        selectedStations: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    onValidate: PropTypes.func,
}

const HydrometryGraphicApp = ({

}) => {
    const {
        hydrometricStations,
        hydroMeasures,
        hydrometryDataTypes,
        cities,
    } = useSelector(store => ({
        hydrometricStations: store.HydrometryReducer.hydrometricStations,
        hydroMeasures: store.HydrometryReducer.hydroMeasures,
        hydrometryDataTypes: store.HydrometryReducer.hydrometryDataTypes,
        cities: store.CityReducer.cities,
    }), shallowEqual)

    const defaultFilter = useMemo(() => ({
        // startDate: moment().subtract(36, 'month').valueOf(),
        endDate: moment().valueOf(),
        threshold: null,
        graphicType: 'line',
        dataType: 4,
        groupFunc: 'MAX',
        displayMode: 'normal',
        selectedStations: [],
    }), [])

    const [filter, setFilter] = useState(defaultFilter)
    const [dataLoaded, setDataLoaded] = useState(false)
    const [progress, setProgress] = useState(0)
    const [graphicOptions, setGraphicOptions] = useState({})

    const dispatch = useDispatch()

    useTitle(() => [{
        title: i18n.hydrometry,
        href: 'hydrometry',
    }, {
        title: i18n.overlayGraphic,
        href: 'hydrometry/overlayGraphic',
    }], [])

    useEffect(() => {
        if (!hydrometricStations.length) {
            dispatch(HydrometryAction.fetchHydrometricStations())
        }
        if (!hydrometryDataTypes.length) {
            dispatch(HydrometryAction.fetchHydrometryDataTypes())
        }
        if (!cities.length) {
            dispatch(CityAction.fetchCities())
        }
    }, [])

    useActions(() => ({}), [])

    const getChart = () => {
        if (!dataLoaded && filter.selectedStations.length > 0) {
            return <ProgressCard progress={progress} />
        } else if (dataLoaded) {
            return (
                <Grid2 container columnSpacing={1}>
                    <Grid2 size={9}>
                        <Card className='padding-top-3 padding-bottom-1 padding-left-1'>
                            <CardContent>
                                <HydrometryGraphicChart
                                    filter={filter}
                                    graphicOptions={graphicOptions}
                                />
                            </CardContent>
                        </Card>
                    </Grid2>
                    <Grid2 size={3}>
                        <SimpleOptionsToolPanel
                            updateState={newGraphicOptions => setGraphicOptions(prev => ({ ...prev, ...newGraphicOptions }))}
                        />
                    </Grid2>
                </Grid2>
            )
        }
        return null
    }

    useUpdateEffect(() => {
        const { startDate, endDate, dataType, groupFunc, selectedStations } = filter
        if (selectedStations.length) {
            setDataLoaded(false)
            setProgress(0)
            const hydroIds = selectedStations.map(s => s.id)
            const criterias = {
                dataType,
                groupFunc,
                chartMode: true,
                startDate,
                endDate,
            }
            dispatch(HydrometryAction.loadHydroChronicsMeasures(criterias, hydroIds, setProgress)).then(() => setDataLoaded(true))
        }
    }, [filter])

    const onValidate = (newFilter) => {
        setDataLoaded(false)
        getComponentWithId('#collapse-top-bar').collapse()
        setFilter({ ...filter, ...newFilter })
    }

    return (
        <Stack sx={{ padding: '0 20' }}>
            <HydrometerCollapseFilter
                defaultFilter={defaultFilter}
                onValidate={onValidate}
            />
            {(dataLoaded && !hydroMeasures.length) ?
                <Box sx={{ margin: '30 0' }}><NoDataCard /></Box> : getChart()}
        </Stack>
    )
}


HydrometryGraphicApp.propTypes = {
    stationTypes: PropTypes.arrayOf(PropTypes.instanceOf(DtoStationType)),
    hydrometricStations: arrayOf(DtoHydrometricStation),
    hydrosIndex: objectOf(DtoHydrometricStation),
    filters: PropTypes.arrayOf(PropTypes.instanceOf(DtoFilter)),
    hydroMeasures: arrayOf(DtoHydroMeasures),
    hydrometryDataTypes: PropTypes.arrayOf(PropTypes.instanceOf(DtoParametrageDataType)),
}

export default HydrometryGraphicApp
